import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

/**
 * App-wide: when a text field is focused, keep it above the on-screen keyboard.
 *
 * The native app runs the Capacitor keyboard in `resize: none` mode (see capacitor.config), so the
 * screen is NOT shortened when the keyboard opens and anything near the bottom ends up behind it.
 * Two cases are handled here, for every screen:
 *
 *  1. A field in a scrolling form/modal: its scroll container gets bottom padding equal to the
 *     keyboard (so the last fields can actually scroll up) and the field is scrolled above the
 *     keyboard. The padding is put back when the keyboard closes.
 *  2. A field inside a portaled dropdown card (e.g. a dropdown's search box): the card — an absolutely
 *     positioned child of a fixed portal root — is slid up with a transform just enough that its bottom
 *     clears the keyboard. A transform is used so the many local dropdown positioning hooks, which
 *     rewrite `top`/`maxHeight`, don't undo it. A no-op for cards that already clear the keyboard
 *     (the shared usePortalPickerRect / PortalDropdown hooks do that themselves).
 *
 * Skipped on purpose: pinned bars inside a `position: fixed` ancestor that aren't dropdown cards
 * (search overlays, the chat footer — they lift themselves via useKeyboardLift), and anything marked
 * `data-no-keyboard-scroll`. Native only: in a browser/PWA the browser already resizes the page.
 */

const TEXT_INPUT_TYPES = new Set([
  'text', 'email', 'password', 'search', 'tel', 'url', 'number', 'date', 'time', 'datetime-local', 'month', 'week', '',
]);
/** Visible space kept between the bottom of the field/card and the top of the keyboard. */
const GAP_PX = 24;
const CARD_GAP_PX = 8;
/** A lifted card never goes higher than this (clear of the status bar / header). */
const CARD_MIN_TOP_PX = 56;

type Target = { kind: 'scroller' | 'card'; el: HTMLElement } | null;

let installed = false;
let keyboardHeight = 0;
let activeField: HTMLElement | null = null;
let timers: number[] = [];
/** Scroll containers we padded, with the inline padding they had before so it can be restored. */
const padded = new Map<HTMLElement, { inline: string; basePx: number }>();
/** Dropdown cards we slid up (their transform is cleared when the keyboard closes). */
const liftedCards = new Set<HTMLElement>();

const isTextField = (el: Element | null): el is HTMLElement => {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.hasAttribute('data-no-keyboard-scroll') || el.closest('[data-no-keyboard-scroll]')) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName === 'INPUT') return TEXT_INPUT_TYPES.has((el as HTMLInputElement).type?.toLowerCase() ?? '');
  return el.isContentEditable;
};

/** What has to move for this field to clear the keyboard: its scroll container, its dropdown card, or nothing. */
function findTarget(field: HTMLElement): Target {
  let node: HTMLElement | null = field;
  let below: HTMLElement | null = null; // the ancestor just beneath `node` on the path up from the field
  while (node && node !== document.body && node !== document.documentElement) {
    const cs = getComputedStyle(node);
    if (cs.position === 'fixed') {
      // Inside a fixed portal root: a dropdown card is its absolutely positioned child. Anything else
      // fixed (a pinned bar) lifts itself.
      if (below && getComputedStyle(below).position === 'absolute') return { kind: 'card', el: below };
      return null;
    }

    if (node.tagName === 'ION-CONTENT') {
      // ion-content scrolls inside its shadow root (unless scrollY is off, in which case a wrapper
      // further down the tree — already checked — is the scroller)
      const inner = node.shadowRoot?.querySelector<HTMLElement>('.inner-scroll');
      if (inner && getComputedStyle(inner).overflowY !== 'hidden') return { kind: 'scroller', el: inner };
    } else if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && node.clientHeight > 120) {
      return { kind: 'scroller', el: node };
    }
    below = node;
    node = node.parentElement;
  }
  return null;
}

function padScroller(scroller: HTMLElement) {
  // Remember, once per keyboard session, the inline padding to restore and the stylesheet padding to build on
  if (!padded.has(scroller)) {
    padded.set(scroller, { inline: scroller.style.paddingBottom, basePx: parseFloat(getComputedStyle(scroller).paddingBottom) || 0 });
  }
  scroller.style.paddingBottom = `${padded.get(scroller)!.basePx + keyboardHeight}px`;
}

function restorePadding() {
  padded.forEach(({ inline }, scroller) => {
    if (inline) scroller.style.paddingBottom = inline;
    else scroller.style.removeProperty('padding-bottom');
  });
  padded.clear();
}

function liftCard(card: HTMLElement) {
  const prevShift = Number(card.dataset.kbShift || 0);
  const rect = card.getBoundingClientRect();
  // position the card would have with no shift of ours
  const naturalTop = rect.top + prevShift;
  const naturalBottom = rect.bottom + prevShift;
  const limit = window.innerHeight - keyboardHeight - CARD_GAP_PX;
  const over = naturalBottom - limit;
  const shift = over > 0 ? Math.min(over, Math.max(0, naturalTop - CARD_MIN_TOP_PX)) : 0;
  card.dataset.kbShift = String(shift);
  ensureSlideTransition(card);
  card.style.transform = shift ? `translateY(${-shift}px)` : '';
  if (shift) liftedCards.add(card);
}

function restoreCards() {
  liftedCards.forEach((card) => {
    card.style.transform = '';
    delete card.dataset.kbShift;
  });
  liftedCards.clear();
}

const SLIDE = 'transform 200ms cubic-bezier(.2, .9, .3, 1)';
/** Animate the card's slide (same easing as the search bars' keyboard lift); keeps any transition it already has. */
function ensureSlideTransition(card: HTMLElement) {
  if (card.dataset.kbSlide) return;
  card.dataset.kbSlide = '1';
  const cs = getComputedStyle(card);
  const hasTransition = cs.transitionDuration.split(',').some((d) => parseFloat(d) > 0);
  card.style.transition = hasTransition ? `${cs.transition}, ${SLIDE}` : SLIDE;
}

function keepFieldAboveKeyboard() {
  const field = activeField;
  if (!field || keyboardHeight <= 0 || !field.isConnected) return;
  const target = findTarget(field);
  if (!target) return;

  if (target.kind === 'card') {
    liftCard(target.el);
    return;
  }

  padScroller(target.el);
  const bottom = field.getBoundingClientRect().bottom;
  const limit = window.innerHeight - keyboardHeight - GAP_PX;
  if (bottom > limit) target.el.scrollBy({ top: bottom - limit, behavior: 'smooth' });
}

/**
 * Move right away — this is called from the keyboard's first event, and the move animates alongside
 * the keyboard — then once more after it has settled, as a correction (normally a no-op).
 */
function schedule() {
  timers.forEach((t) => window.clearTimeout(t));
  keepFieldAboveKeyboard();
  timers = [window.setTimeout(keepFieldAboveKeyboard, 350)];
}

export function installFocusAboveKeyboard() {
  if (installed || !Capacitor.isNativePlatform()) return;
  installed = true;

  document.addEventListener('focusin', (e) => {
    const target = (e.composedPath?.()[0] ?? e.target) as Element | null;
    if (!isTextField(target)) return;
    activeField = target;
    if (keyboardHeight > 0) schedule(); // keyboard already open (moving between fields)
  });

  const onShow = (info: { keyboardHeight?: number }) => {
    keyboardHeight = Math.round(info.keyboardHeight || 0);
    if (activeField) schedule();
  };
  const onHide = () => {
    keyboardHeight = 0;
    activeField = null;
    timers.forEach((t) => window.clearTimeout(t));
    restorePadding();
    restoreCards();
  };

  try {
    Keyboard.addListener('keyboardWillShow', onShow).catch(() => { });
    Keyboard.addListener('keyboardDidShow', onShow).catch(() => { });
    Keyboard.addListener('keyboardWillHide', onHide).catch(() => { });
    Keyboard.addListener('keyboardDidHide', onHide).catch(() => { });
  } catch { /* plugin unavailable */ }
}
